import { title } from "process";

import { type ChartOptions } from "chart.js";
import moment from "moment";

import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { type FullBudgetMixResponse } from "@north-beam/nb-common";

import { type Kpi } from "../../client-setting/types";

import { chartTooltip } from "./tooltip";

export const getChartOptions = ({
  user,
  selectedKpi,
  selectedBudgetMix,
  useDefaultTooltip = false,
}: {
  useDefaultTooltip?: boolean;
  user: User;
  selectedKpi: Kpi;
  selectedBudgetMix: FullBudgetMixResponse;
}): ChartOptions<"line"> => {
  const isRevenue = selectedKpi.kpi_type === "price_value";
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: useDefaultTooltip ? true : false,
        displayColors: false,
        callbacks: {
          title: function (context: any) {
            const start = moment(context[0].label);

            return `${start.format("MMM DD")} - ${start
              .add(6, "days")
              .format("MMM DD")}`;
          },
          label: function (context: any) {
            const y = context.parsed.y;
            let value = isRevenue
              ? MetricFormatters.currency(y, user.currencies.currentCurrency, {
                  precision: "exact",
                })
              : MetricFormatters.decimal(y, user.currencies.currentCurrency, {
                  precision: "exact",
                });

            if (context.dataset.label === "Spend") {
              value = MetricFormatters.currency(
                y,
                user.currencies.currentCurrency,
                { precision: "exact" },
              );
            }

            let label =
              context.dataset.id === "RANGE"
                ? `Forecast ${context.dataset.label}`
                : context.dataset.label || "";

            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += value;
            }
            return label;
          },
        },
        ...(useDefaultTooltip
          ? {}
          : {
              external: chartTooltip,
            }),
        user,
        isRevenue,
        selectedBudgetMix,
      } as any,
      legend: { display: false },
    },
    layout: {
      padding: { left: 8, right: 20 },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          drawTicks: false,
          drawBorder: false,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 18,
          font: { size: 11 },
          callback: (val: string | number) => {
            if (selectedKpi.kpi_type === "price_value") {
              return MetricFormatters.currency(
                Number(val),
                user.currencies.currentCurrency,
                { precision: "approximate" },
              );
            } else {
              return MetricFormatters.decimal(
                Number(val),
                user.currencies.currentCurrency,
                { precision: "approximate" },
              );
            }
          },
        },
      },
      y1: {
        position: "right",
        beginAtZero: true,
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: false,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 18,
          font: { size: 11 },
          callback: (val: string | number) =>
            MetricFormatters.currency(
              Number(val),
              user.currencies.currentCurrency,
              { precision: "approximate" },
            ),
        },
      },
      x: {
        grid: {
          drawBorder: false,
          drawTicks: false,
          drawOnChartArea: false,
        },
        ticks: {
          padding: 18,
          autoSkip: true,
          font: { size: 11 },
        },
      },
    },
  };
};
